// Purely visual interaction layer: dust trail, magnetic buttons,
// card tilt, scroll reveals and nav state. Touches presentation only — no content or data.

type Particle = { x: number; y: number; vx: number; vy: number; life: number; max: number; size: number; kind: 'smoke' | 'speck'; seed: number };

export function initFx(root: HTMLElement): () => void {
  const win = window;
  const doc = document.documentElement;
  const reduced = win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = win.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const cleanups: Array<() => void> = [];
  const listen = (target: EventTarget, type: string, fn: (e: any) => void, opts?: AddEventListenerOptions) => {
    target.addEventListener(type, fn, opts);
    cleanups.push(() => target.removeEventListener(type, fn, opts));
  };

  splitHeadline(root);

  // ---- Scroll reveals ----
  if (!reduced && 'IntersectionObserver' in win) {
    const targets = root.querySelectorAll<HTMLElement>(
      '.hero .eyebrow, .lead, .hero-actions, .highlight, .hero-card, .section h2, .stack-intro, .about > div > p, .about-card,' +
      '.work-card, .service, .skill-group, .timeline-card, .contact > div > p, .contact-card, .footer'
    );
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target as HTMLElement;
        el.classList.add('is-in');
        io.unobserve(el);
        // Hand the element back to its own transitions once the reveal is done.
        const delay = parseInt(el.style.getPropertyValue('--d') || '0', 10);
        win.setTimeout(() => {
          el.classList.remove('reveal', 'is-in');
          el.style.removeProperty('--d');
        }, 1300 + delay);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    targets.forEach(el => {
      const siblings = Array.from(el.parentElement?.children ?? []).filter(c => c.classList.contains(el.classList[0]));
      const index = Math.max(0, siblings.indexOf(el));
      el.style.setProperty('--d', `${(index % 8) * 70}ms`);
      el.classList.add('reveal');
      io.observe(el);
    });
    cleanups.push(() => io.disconnect());
  }

  // ---- Nav: scrolled state, progress bar, active section ----
  const nav = root.querySelector<HTMLElement>('.nav');
  const progress = root.querySelector<HTMLElement>('.fx-progress');
  const onScroll = () => {
    const y = win.scrollY;
    nav?.classList.toggle('nav--scrolled', y > 40);
    if (y < win.innerHeight * 0.4) {
      root.querySelectorAll('.nav-links a.is-active').forEach(a => a.classList.remove('is-active'));
    }
    if (progress) {
      const max = doc.scrollHeight - win.innerHeight;
      progress.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
    }
  };
  onScroll();
  listen(win, 'scroll', onScroll, { passive: true });

  if ('IntersectionObserver' in win) {
    const links = new Map<string, HTMLElement>();
    root.querySelectorAll<HTMLAnchorElement>('.nav-links a[href^="#"]').forEach(a => links.set(a.getAttribute('href')!.slice(1), a));
    const sectionIo = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const link = links.get(entry.target.id);
        if (link && entry.isIntersecting) {
          links.forEach(l => l.classList.remove('is-active'));
          link.classList.add('is-active');
        }
      });
    }, { rootMargin: '-45% 0px -50% 0px' });
    links.forEach((_, id) => {
      const section = root.querySelector(`#${id}`);
      if (section) sectionIo.observe(section);
    });
    cleanups.push(() => sectionIo.disconnect());
  }

  if (!finePointer) {
    return () => cleanups.forEach(fn => fn());
  }

  // ---- Pointer-driven effects (desktop only) ----
  const canvas = root.querySelector<HTMLCanvasElement>('.fx-dust');
  const ctx = canvas?.getContext('2d') ?? null;

  // Soft, pre-rendered smoke puffs (one per theme), scaled per particle when drawn
  const makePuff = (core: string, mid: string, edge: string) => {
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g2d = c.getContext('2d');
    if (g2d) {
      const g = g2d.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, core);
      g.addColorStop(0.35, mid);
      g.addColorStop(0.7, edge);
      g.addColorStop(1, 'rgba(0, 0, 0, 0)');
      g2d.fillStyle = g;
      g2d.fillRect(0, 0, 64, 64);
    }
    return c;
  };
  const puffDark = makePuff('rgba(240, 236, 226, 1)', 'rgba(225, 230, 210, 0.55)', 'rgba(212, 255, 63, 0.12)');
  const puffLight = makePuff('rgba(70, 66, 56, 0.9)', 'rgba(90, 96, 70, 0.45)', 'rgba(74, 102, 0, 0.12)');
  const isLight = () => doc.dataset['theme'] === 'light';

  const mouse = { x: win.innerWidth / 2, y: win.innerHeight / 2 };
  const particles: Particle[] = [];
  let last = { ...mouse };
  let raf = 0;

  const resize = () => {
    if (!canvas || !ctx) return;
    const dpr = Math.min(win.devicePixelRatio || 1, 2);
    canvas.width = win.innerWidth * dpr;
    canvas.height = win.innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  listen(win, 'resize', resize);

  listen(win, 'pointermove', (e: PointerEvent) => {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
    if (!reduced) {
      const dist = Math.hypot(mouse.x - last.x, mouse.y - last.y);
      const steps = Math.min(4, Math.floor(dist / 10));
      for (let i = 0; i < steps && particles.length < 90; i++) {
        // Spread puffs along the path travelled so fast moves leave a continuous trail
        const k = (i + 1) / steps;
        const x = last.x + (mouse.x - last.x) * k;
        const y = last.y + (mouse.y - last.y) * k;
        particles.push({
          x: x + (Math.random() - 0.5) * 4,
          y: y + (Math.random() - 0.5) * 4,
          vx: (Math.random() - 0.5) * 0.35,
          vy: -0.25 - Math.random() * 0.35,
          life: 0,
          max: 70 + Math.random() * 50,
          size: 7 + Math.random() * 7,
          kind: 'smoke',
          seed: Math.random() * Math.PI * 2
        });
        if (Math.random() < 0.18) {
          particles.push({
            x: x + (Math.random() - 0.5) * 10,
            y: y + (Math.random() - 0.5) * 10,
            vx: (Math.random() - 0.5) * 0.5,
            vy: -0.2 - Math.random() * 0.4,
            life: 0,
            max: 40 + Math.random() * 40,
            size: 0.6 + Math.random() * 1.1,
            kind: 'speck',
            seed: 0
          });
        }
      }
      if (steps) last = { x: mouse.x, y: mouse.y };
    }
    if (particles.length) start();
  }, { passive: true });

  const tick = () => {
    raf = 0;
    if (ctx && canvas) {
      ctx.clearRect(0, 0, win.innerWidth, win.innerHeight);
      const light = isLight();
      const puff = light ? puffLight : puffDark;
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life++;
        const t = p.life / p.max;
        if (t >= 1) {
          particles.splice(i, 1);
          continue;
        }
        if (p.kind === 'smoke') {
          // Gentle curl so the plume swirls instead of rising in a straight line
          p.x += p.vx + Math.sin(p.seed + p.life * 0.05) * 0.18;
          p.y += p.vy;
          p.vx *= 0.97;
          const radius = p.size * (1 + t * 3.2);
          // Fade in quickly, then dissolve slowly
          ctx.globalAlpha = Math.min(1, t * 6) * (1 - t) * (1 - t) * 0.28;
          ctx.drawImage(puff, p.x - radius, p.y - radius, radius * 2, radius * 2);
        } else {
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.985;
          ctx.globalAlpha = (1 - t) * 0.75;
          ctx.fillStyle = light ? '#4a6600' : '#d4ff3f';
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    }

    // Keep animating only while smoke is still drifting; the last frame clears the canvas.
    if (particles.length) start();
  };
  const start = () => {
    if (!raf) raf = win.requestAnimationFrame(tick);
  };
  cleanups.push(() => win.cancelAnimationFrame(raf));

  if (reduced) {
    return () => cleanups.forEach(fn => fn());
  }

  // ---- Magnetic buttons ----
  root.querySelectorAll<HTMLElement>('.btn, .cta, .contact-icon-btn, .project-link').forEach(el => {
    const strength = el.classList.contains('contact-icon-btn') ? 0.4 : 0.28;
    listen(el, 'pointermove', (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      el.style.transform = `translate3d(${dx * strength}px, ${dy * strength}px, 0)`;
    });
    listen(el, 'pointerleave', () => el.style.removeProperty('transform'));
  });

  // ---- 3D tilt + local spotlight on cards ----
  root.querySelectorAll<HTMLElement>('.work-card, .hero-card, .skill-group').forEach(el => {
    const max = el.classList.contains('work-card') ? 7 : el.classList.contains('hero-card') ? 5 : 4;
    listen(el, 'pointermove', (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      el.style.setProperty('--rx', `${(0.5 - py) * max}deg`);
      el.style.setProperty('--ry', `${(px - 0.5) * max}deg`);
      el.style.setProperty('--mx', `${px * 100}%`);
      el.style.setProperty('--my', `${py * 100}%`);
    });
    listen(el, 'pointerleave', () => {
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    });
  });

  return () => cleanups.forEach(fn => fn());
}

// Wraps each word of the hero headline in spans for a staggered entrance.
// The text itself is untouched: same words, same order, same spaces.
function splitHeadline(root: HTMLElement): void {
  const el = root.querySelector<HTMLElement>('[data-split]');
  if (!el || el.dataset['splitDone']) return;
  const words = (el.textContent ?? '').trim().split(/\s+/);
  const accentFrom = words.length - Number(el.dataset['accentLast'] ?? 0);
  el.textContent = '';
  words.forEach((word, i) => {
    const outer = document.createElement('span');
    outer.className = i >= accentFrom ? 'w w-accent' : 'w';
    const inner = document.createElement('span');
    inner.className = 'w-i';
    inner.style.setProperty('--i', String(i));
    inner.textContent = word;
    outer.appendChild(inner);
    el.appendChild(outer);
    if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
  });
  el.dataset['splitDone'] = 'true';
}
